import { type ReactNode } from 'react';

import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';

export type AgencyRulesPreviewCardProps = Readonly<{
    readonly agencyName: string;
    readonly rules: ReadonlyArray<string>;
    readonly totalRuleCount: number;
    readonly onViewAll: () => void;
}>;

export function AgencyRulesPreviewCard({
    agencyName,
    rules,
    totalRuleCount,
    onViewAll,
}: AgencyRulesPreviewCardProps): ReactNode {
    const remainingRuleCount = Math.max(0, totalRuleCount - rules.length);
    const ruleCountLabel = `${totalRuleCount} active ${totalRuleCount === 1 ? 'rule' : 'rules'}`;

    return (
        <article
            aria-label={`${agencyName} rules preview`}
            className="bg-card w-full min-w-0 rounded-lg border px-4 py-3.5"
        >
            <header>
                <h3 className="text-foreground text-v3-chat font-bold">
                    {agencyName}
                </h3>
                <p className="text-muted-foreground text-v3-chat-meta mt-0.5">
                    {ruleCountLabel}
                </p>
            </header>
            <ul className="text-foreground marker:text-muted-foreground text-v3-chat mt-3.5 list-disc space-y-2 pl-4">
                {/* A rule can cite the playbook it came from, never its id. */}
                {rules.map((rule) => (
                    <li key={rule} className="pl-1">
                        <ResolvedReferenceText text={rule} />
                    </li>
                ))}
            </ul>
            <footer className="mt-3.5 flex min-h-8 items-center gap-3">
                {remainingRuleCount > 0 && (
                    <span className="text-muted-foreground text-v3-chat-meta">
                        + {remainingRuleCount} more{' '}
                        {remainingRuleCount === 1 ? 'rule' : 'rules'}
                    </span>
                )}
                <button
                    type="button"
                    onClick={onViewAll}
                    className="text-agent-action hover:bg-agent-action-wash focus-visible:ring-agent-action/30 text-v3-chat ml-auto min-h-8 rounded-md px-2 font-semibold focus-visible:ring-2 focus-visible:outline-none dark:text-violet-300"
                >
                    View all rules <span aria-hidden="true">→</span>
                </button>
            </footer>
        </article>
    );
}
